import React from 'react'
import styled from 'styled-components'
import { Check, X } from 'lucide-react'
import type { NodeChatToolCall } from './types'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'

/** Map internal field names → user-facing labels */
const FIELD_LABELS: Record<string, string> = {
  label: 'Description',
  companyName: 'Project Name',
  businessModelAndValueProp: 'What are you building?',
  capabilitiesAndAssets: 'Available Strengths',
  hardConstraints: 'Project Boundaries',
  goals: 'Who is it for?',
  productType: 'Format',
  name: 'Name',
  age: 'Age',
  occupation: 'Occupation',
}

interface ToolCallCardProps {
  toolCall: NodeChatToolCall
  /** Current value of the field on the node */
  currentValue: string
  onApprove: () => void
  onReject: () => void
}

export const ToolCallCard = React.memo(
  ({ toolCall, currentValue, onApprove, onReject }: ToolCallCardProps): React.ReactElement => {
    const fieldLabel = FIELD_LABELS[toolCall.args.field] ?? toolCall.args.field
    const isResolved = toolCall.status !== 'pending'

    return (
      <Card $status={toolCall.status}>
        <Header>
          <FieldLabel>Edit: {fieldLabel}</FieldLabel>
          {isResolved && (
            <StatusBadge $approved={toolCall.status === 'approved'}>
              {toolCall.status === 'approved' ? 'Applied' : 'Skipped'}
            </StatusBadge>
          )}
        </Header>

        {toolCall.args.reasoning && <Reasoning>{toolCall.args.reasoning}</Reasoning>}

        <DiffArea>
          {currentValue && toolCall.status === 'pending' && (
            <DiffBlock $type="before">
              <DiffLabel>Before</DiffLabel>
              <DiffText>{currentValue}</DiffText>
            </DiffBlock>
          )}
          <DiffBlock $type="after">
            <DiffLabel>{toolCall.status === 'pending' ? 'After' : 'Value'}</DiffLabel>
            <DiffText>{toolCall.args.value}</DiffText>
          </DiffBlock>
        </DiffArea>

        {!isResolved && (
          <Actions>
            <ActionButton $variant="approve" onClick={onApprove}>
              <Check size={12} strokeWidth={2.5} />
              Apply
            </ActionButton>
            <ActionButton $variant="reject" onClick={onReject}>
              <X size={12} strokeWidth={2.5} />
              Skip
            </ActionButton>
          </Actions>
        )}
      </Card>
    )
  }
)

const Card = styled.div<{ $status: string }>`
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-chat-card-bg);
  padding: 8px 10px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  opacity: ${(p) => (p.$status === 'rejected' ? 0.5 : 1)};
  max-width: 95%;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-bottom: 4px;
`

const FieldLabel = styled.span`
  font-weight: ${FONT_WEIGHT.semibold};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
`

const StatusBadge = styled.span<{ $approved: boolean }>`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  padding: 1px 6px;
  border-radius: 4px;
  background: ${(p) => (p.$approved ? 'rgba(16, 185, 129, 0.1)' : 'rgba(107, 114, 128, 0.1)')};
  color: ${(p) => (p.$approved ? 'var(--color-success, #10b981)' : 'var(--color-text-muted)')};
`

const Reasoning = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-muted);
  font-style: italic;
  margin-bottom: 6px;
  line-height: 1.3;
`

const DiffArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const DiffBlock = styled.div<{ $type: 'before' | 'after' }>`
  border-radius: 4px;
  padding: 5px 7px;
  background: ${(p) =>
    p.$type === 'before' ? 'rgba(239, 68, 68, 0.06)' : 'rgba(16, 185, 129, 0.06)'};
  border-left: 2px solid
    ${(p) => (p.$type === 'before' ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)')};
`

const DiffLabel = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-muted);
  margin-bottom: 2px;
`

const DiffText = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.4;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-wrap: break-word;
`

const Actions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 6px;
`

const ActionButton = styled.button<{ $variant: 'approve' | 'reject' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 5px;
  border: none;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s;
  background: ${(p) =>
    p.$variant === 'approve' ? 'var(--color-primary)' : 'var(--color-chat-bubble-bg)'};
  color: ${(p) => (p.$variant === 'approve' ? '#fff' : 'var(--color-text-secondary)')};
  border: ${(p) => (p.$variant === 'approve' ? 'none' : '1px solid var(--color-border)')};

  &:hover {
    opacity: 0.85;
  }
`
